/**
 * Curated Library — the curated_wisdom editor.
 *
 * LifeScribe-written wisdom a member can send when they would rather borrow the
 * words than record their own, so the editor is built around the writing: a
 * live character/word count and a Georgia-serif preview pane rendered the way
 * the member sees it. Tone is a free-text column in the DB (varchar 50); the
 * dropdown offers the house tones and allows anything already stored.
 */

import React from 'react';
import {
  Box,
  Button,
  Alert,
  Chip,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import EmptyState from '../../common/EmptyState';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  WisdomCategory,
  CuratedWisdom,
  CuratedInput,
} from '../../../services/lifeAdviceAdminService';
import {
  PageHeader,
  errorMessage,
  writeErrorMessage,
  isBackendDown,
  countWords,
} from '../common/lifeAdviceUi';
import LifeAdviceActionConfirm from '../common/LifeAdviceActionConfirm';

const PAGE_LIMIT = 500; // the service's max for /curated

const HOUSE_TONES = ['warm', 'plainspoken', 'reflective', 'encouraging', 'candid', 'tender'];

interface DraftState {
  category_id: string;
  title: string;
  body: string;
  author: string;
  tone: string;
  is_active: boolean;
  sort_order: number;
}

const BLANK_DRAFT: DraftState = {
  category_id: '',
  title: '',
  body: '',
  author: 'LifeScribe Wisdom',
  tone: 'warm',
  is_active: true,
  sort_order: 0,
};

const CuratedLibraryPage: React.FC = () => {
  const [categories, setCategories] = React.useState<WisdomCategory[]>([]);
  const [rows, setRows] = React.useState<CuratedWisdom[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = React.useState('');

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<DraftState>(BLANK_DRAFT);
  const [saving, setSaving] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | null>(null);

  const [confirming, setConfirming] = React.useState<CuratedWisdom | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      const [cats, page] = await Promise.all([
        lifeAdviceApi.categories.list(),
        lifeAdviceApi.curated.list({
          ...(categoryFilter ? { category_id: categoryFilter } : {}),
          limit: PAGE_LIMIT,
          page: 1,
        }),
      ]);
      setCategories(cats);
      setRows(page.rows);
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load the curated library.'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const toneOptions = React.useMemo(() => {
    const stored = rows.map((r) => r.tone).filter((t): t is string => !!t);
    return Array.from(new Set([...HOUSE_TONES, ...stored]));
  }, [rows]);

  const openNew = () => {
    setEditingId(null);
    setDraft({ ...BLANK_DRAFT, category_id: categoryFilter || categories[0]?.id || '' });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (w: CuratedWisdom) => {
    setEditingId(w.id);
    setDraft({
      category_id: w.category_id,
      title: w.title,
      body: w.body,
      author: w.author || 'LifeScribe Wisdom',
      tone: w.tone || '',
      is_active: w.is_active,
      sort_order: w.sort_order,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!draft.category_id) {
      setDialogError('Pick a wisdom pillar.');
      return;
    }
    if (!draft.title.trim()) {
      setDialogError('Title is required.');
      return;
    }
    if (!draft.body.trim()) {
      setDialogError('The wisdom body is required.');
      return;
    }
    setSaving(true);
    setDialogError(null);
    const payload: CuratedInput = {
      category_id: draft.category_id,
      title: draft.title,
      body: draft.body,
      author: draft.author || 'LifeScribe Wisdom',
      tone: draft.tone || null,
      is_active: draft.is_active,
      sort_order: draft.sort_order,
    };
    try {
      if (editingId) {
        await lifeAdviceApi.curated.update(editingId, payload);
        setToast('Curated wisdom saved');
      } else {
        await lifeAdviceApi.curated.create(payload);
        setToast('Curated wisdom created');
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      setDialogError(writeErrorMessage(e, 'Could not save this piece.'));
    } finally {
      setSaving(false);
    }
  };

  const removeCurated = async () => {
    if (!confirming) return;
    setConfirmBusy(true);
    try {
      const result = await lifeAdviceApi.curated.remove(confirming.id);
      setToast(
        result.deactivated
          ? 'This piece is in use by member entries, so it was deactivated instead of deleted'
          : 'Curated wisdom deleted'
      );
      setConfirming(null);
      reload();
    } catch (e) {
      setToast(writeErrorMessage(e, 'Could not delete this piece.'));
      setConfirming(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const columns: GridColDef<CuratedWisdom>[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams<CuratedWisdom, string>) => (
        <Tooltip title={params.row.body.slice(0, 220)}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'category_name',
      headerName: 'Pillar',
      width: 190,
      valueGetter: (_value, row) => row.category_name || row.category_slug || '—',
    },
    {
      field: 'tone',
      headerName: 'Tone',
      width: 140,
      renderCell: (params: GridRenderCellParams<CuratedWisdom, string | null>) =>
        params.value ? <Chip size="small" label={params.value} variant="outlined" /> : <span>—</span>,
    },
    { field: 'author', headerName: 'Author', width: 180 },
    {
      field: 'words',
      headerName: 'Words',
      width: 90,
      type: 'number',
      sortable: false,
      valueGetter: (_value, row) => countWords(row.body || ''),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams<CuratedWisdom, boolean>) =>
        params.value ? (
          <Chip size="small" label="Active" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Hidden" variant="outlined" />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<CuratedWisdom>) => (
        <Box>
          <IconButton
            size="small"
            aria-label={`Edit ${params.row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              openEdit(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${params.row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(params.row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  const bodyChars = draft.body.length;
  const bodyWords = countWords(draft.body);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<MenuBookIcon sx={{ fontSize: 32, color: brandColors.richBurgundy }} />}
        title="Curated Library"
        subtitle="LifeScribe-written wisdom members can send when they would rather borrow the words."
        helpId="lifeadvice-curated"
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
              New piece
            </Button>
          </Stack>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Pillar"
          size="small"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All pillars</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.icon} {c.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {!loading && rows.length === 0 && !error ? (
        <EmptyState
          icon="empty_box"
          title="Nothing curated yet"
          description="Write the first piece of LifeScribe wisdom for this pillar — members see it as an alternative to recording their own."
          action={{ label: 'New piece', onClick: openNew, variant: 'contained' }}
        />
      ) : (
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            disableRowSelectionOnClick
            onRowClick={(params) => openEdit(params.row as CuratedWisdom)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {/* ── Editor with live preview ─────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit curated wisdom' : 'New curated wisdom'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Wisdom pillar"
                value={draft.category_id}
                onChange={(e) => setDraft({ ...draft, category_id: e.target.value })}
                fullWidth
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.icon} {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Tone"
                value={draft.tone}
                onChange={(e) => setDraft({ ...draft, tone: e.target.value })}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">No tone</MenuItem>
                {toneOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              fullWidth
              autoFocus
            />

            <TextField
              label="Wisdom"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              multiline
              minRows={7}
              fullWidth
              helperText={`${bodyWords} words · ${bodyChars} characters`}
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Member preview
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  mt: 0.5,
                  p: 2.5,
                  backgroundColor: brandColors.softCream,
                  borderRadius: '12px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 20,
                    fontWeight: 600,
                    color: brandColors.deepTeal,
                    mb: 1,
                  }}
                >
                  {draft.title || 'Untitled'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 16,
                    lineHeight: 1.75,
                    color: '#2B2B2B',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {draft.body || 'The wisdom you write appears here, set the way a member reads it.'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: 13,
                    fontStyle: 'italic',
                    color: brandColors.slateGray,
                    mt: 2,
                  }}
                >
                  — {draft.author || 'LifeScribe Wisdom'}
                </Typography>
              </Paper>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Author"
                value={draft.author}
                onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                fullWidth
              />
              <TextField
                label="Sort order"
                type="number"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
                sx={{ maxWidth: 200 }}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                />
              }
              label="Offered to members"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save wisdom'}
          </Button>
        </DialogActions>
      </Dialog>

      <LifeAdviceActionConfirm
        open={!!confirming}
        destructive
        loading={confirmBusy}
        title="Delete this curated piece?"
        message={confirming ? `"${confirming.title}" will be removed from the library.` : ''}
        caption="If a member has already sent this piece the service deactivates it instead of deleting, so their entry keeps its source."
        confirmLabel="Delete piece"
        pendingLabel="Deleting…"
        onConfirm={removeCurated}
        onCancel={() => setConfirming(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CuratedLibraryPage;
